import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Loader2, Pencil, Trash2, QrCode } from "lucide-react";

const EMPTY = { name: "", mobile: "", village: "", address: "", aadhaar: "", bank_name: "", account_number: "", ifsc: "", photo: "", farmer_code: "", status: "active" };

export default function Farmers() {
  const { user } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const canEdit = user.role === "tenant_owner";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [qrFarmer, setQrFarmer] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["farmers", page, search],
    queryFn: async () => (await api.get("/farmers", { params: { page, page_size: 10, search } })).data,
  });

  const save = useMutation({
    mutationFn: async (body) => editing ? (await api.put(`/farmers/${editing}`, body)).data : (await api.post("/farmers", body)).data,
    onSuccess: () => { toast.success(editing ? "Farmer updated" : "Farmer added"); close(); qc.invalidateQueries({ queryKey: ["farmers"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/farmers/${id}`)).data,
    onSuccess: () => { toast.success("Farmer deleted"); qc.invalidateQueries({ queryKey: ["farmers"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (f) => { setEditing(f.id); setForm({ ...EMPTY, ...f }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="farmers-page">
      <PageHeader
        title={t("farmers.title")}
        subtitle={t("farmers.subtitle")}
        breadcrumb={[t("crumb.center"), t("farmers.title")]}
        actions={canEdit && <Button onClick={openNew} data-testid="add-farmer-btn"><Plus className="mr-1.5 h-4 w-4" /> {t("farmers.add")}</Button>}
      />

      <Card>
        <div className="border-b p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={t("farmers.searchPlaceholder")} value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="farmer-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("farmers.farmer")}</TableHead>
                <TableHead>{t("farmers.code")}</TableHead>
                <TableHead>{t("farmers.village")}</TableHead>
                <TableHead>{t("common.mobile")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8" /></TableCell></TableRow>)
                : (data?.items || []).map((f) => (
                    <TableRow key={f.id} className="hover:bg-muted/50" data-testid={`farmer-row-${f.farmer_code}`}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-8 w-8"><AvatarImage src={f.photo} /><AvatarFallback className="bg-secondary/30 text-xs">{f.name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                          <span className="font-medium">{f.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="tnum">{f.farmer_code}</TableCell>
                      <TableCell>{f.village}</TableCell>
                      <TableCell className="tnum">{f.mobile}</TableCell>
                      <TableCell><Badge variant={f.status === "active" ? "default" : "secondary"}>{f.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setQrFarmer(f)} data-testid={`qr-farmer-${f.farmer_code}`}><QrCode className="h-4 w-4" /></Button>
                        {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-farmer-${f.farmer_code}`}><Pencil className="h-4 w-4" /></Button>}
                        {canEdit && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-farmer-${f.farmer_code}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete {f.name}?</AlertDialogTitle><AlertDialogDescription>This soft-deletes the farmer record.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => del.mutate(f.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              {!isLoading && data?.items?.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">{t("farmers.none")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <TablePagination page={page} pages={data?.pages} total={data?.total} onPage={setPage} />
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("farmers.edit") : t("farmers.add")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            {[["name", "Name *"], ["farmer_code", "Farmer Code (auto)"], ["mobile", "Mobile"], ["village", "Village"],
              ["aadhaar", "Aadhaar"], ["bank_name", "Bank Name"], ["account_number", "Account Number"], ["ifsc", "IFSC"],
              ["photo", "Photo URL"], ["address", "Address"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label><Input value={form[k]} onChange={set(k)} data-testid={`farmer-${k}-input`} /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>{t("common.cancel")}</Button>
            <Button onClick={() => save.mutate(form)} disabled={!form.name || save.isPending} data-testid="submit-farmer-btn">
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? t("common.update") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrFarmer} onOpenChange={(o) => !o && setQrFarmer(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">{qrFarmer?.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {qrFarmer && <div className="rounded-lg bg-white p-4"><QRCodeCanvas value={JSON.stringify({ code: qrFarmer.farmer_code, name: qrFarmer.name })} size={180} /></div>}
            <p className="text-sm text-muted-foreground">Code: <b className="text-foreground">{qrFarmer?.farmer_code}</b></p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
